import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, CheckIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, LinkButton } from '../../components/ui/Button';
import { EnquiryImages } from '../../components/admin/EnquiryImages';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDateTime, telHref } from '../../utils/format';
import type { Enquiry } from '../../types/admin';
const ease = [0.23, 1, 0.32, 1] as const;
const statusStyles: Record<string, string> = {
  'Ny henvendelse': 'border-[#e6cfc2] bg-accent-soft text-accent-deep',
  Accepteret: 'border-ink/15 bg-ink text-white',
  Behandlet: 'border-line bg-canvas text-ink-soft',
  Afvist: 'border-danger/30 bg-danger/10 text-danger'
};
export function Enquiries() {
  const {
    enquiries,
    dataLoading,
    createCaseFromEnquiry,
    createQuoteFromEnquiry,
    acceptEnquiry,
    deleteEnquiry
  } = useAdminData();
  const navigate = useNavigate();
  const [working, setWorking] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Enquiry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [actionError, setActionError] = useState('');
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-872" data-mp="3fyb6qk0t794t">
      <p className="text-sm text-ink-muted" data-id="element-873" data-mp="3fzmaiju98gvx">Fra formularen “Fortæl om din opgave”</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-874" data-mp="3tuy2ijh0zt95">
        Henvendelser
      </h1>

      {accepted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-875" data-mp="195ijwbrynaul">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-876" data-mp="1uu6xdv318fj">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-877" data-mp="6s445fopqodx" />
          </span>
          Henvendelsen er accepteret
        </p>}

      {actionError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-878" data-mp="fttsudr7n980">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-879" data-mp="3fas6nqhphsqi" />
          {actionError}
        </p>}

      {deleted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-880" data-mp="3oa5eyfu3gb9n">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-881" data-mp="3t2q6tbtfweaj">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-882" data-mp="30kb423tc4ws1" />
          </span>
          Henvendelsen er slettet
        </p>}

      {dataLoading && enquiries.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-883" data-mp="2syfp2ghi0sne">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-884" data-mp="2o2i602eup86x" />
          Henter henvendelser …
        </p>}

      <ul className="mt-7 grid gap-5" data-id="element-885" data-mp="2f10vr0o4k0q5">
        {enquiries.map(enquiry => <li key={enquiry.id} className="rounded-card border border-line bg-white p-5 md:p-6" data-id="element-886" data-mp="2f34ul47629jk">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-887" data-mp="2a40ncj3sylvv">
              <div data-id="element-888" data-mp="259o3i7yez8jl">
                <p className="text-xs text-ink-muted" data-id="element-889" data-mp="2594l6q4uqi51">{formatDateTime(enquiry.createdAt) || 'Netop nu'}</p>
                <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-ink" data-id="element-890" data-mp="209rx6228v50v">{enquiry.name}</h2>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[enquiry.status] ?? 'border-line bg-canvas text-ink-soft'}`} data-id="element-891" data-mp="1rbzfir91vlab">
                {enquiry.status}
              </span>
            </div>

            <dl className="mt-4 grid gap-3 text-[15px] sm:grid-cols-3" data-id="element-892" data-mp="3re7cej72clyr">
              <div data-id="element-893" data-mp="3rfikgv7jkfvo">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-894" data-mp="3cpb8myc1hv3y">Telefon</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-895" data-mp="3hnmi37yfa3ln">
                  <PhoneIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-896" data-mp="3hltczx3gjrfr" />
                  {enquiry.phone || '—'}
                </dd>
              </div>
              <div data-id="element-897" data-mp="37qhs0x1ghnzk">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-898" data-mp="37r1dgnqbeuic">E-mail</dt>
                <dd className="mt-1 flex items-center gap-1.5 break-all text-ink" data-id="element-899" data-mp="2tri822ow1x9q">
                  <MailIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-900" data-mp="2yptee3fz5pn7" />
                  {enquiry.email || '—'}
                </dd>
              </div>
              <div data-id="element-901" data-mp="wy6v6o2bqz1a">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-902" data-mp="wwlxwqoml1zq">Adresse / område</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-903" data-mp="2hghf4ivn8t41">
                  <MapPinIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-904" data-mp="2ch4qptp4jdzc" />
                  {enquiry.address || '—'}
                </dd>
              </div>
            </dl>

            <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-[15px] leading-relaxed text-ink-soft" data-id="element-905" data-mp="2mf2gwmqjnkul">
              {enquiry.description}
            </p>

            <EnquiryImages images={enquiry.images} data-id="element-906" data-mp="308a6y9yz88a6" />

            <div className="mt-5 flex flex-wrap gap-3" data-id="element-907" data-mp="2v62qb737c41i">
              {enquiry.status === 'Ny henvendelse' && <Button size="sm" disabled={accepting === enquiry.id} onClick={async () => {
            if (accepting) return;
            setAccepting(enquiry.id);
            setActionError('');
            setDeleted(false);
            const result = await acceptEnquiry(enquiry.id);
            setAccepting(null);
            if (!result.ok) {
              setActionError(result.error ?? 'Henvendelsen kunne ikke accepteres. Prøv igen.');
              return;
            }
            setAccepted(true);
          }} data-id="element-908" data-mp="3a32277536j1s">
                  {accepting === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-909" data-mp="2jesiz2578qz5" />
                      Accepterer …
                    </> : 'Accepter henvendelse'}
                </Button>}

              <Button variant="secondary" size="sm" disabled={working === `quote-${enquiry.id}`} onClick={async () => {
            setWorking(`quote-${enquiry.id}`);
            setActionError('');
            const created = await createQuoteFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Tilbuddet kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/tilbud/${created.id}`);
          }} data-id="element-910" data-mp="2s8x5j9davg8v">
                {working === `quote-${enquiry.id}` ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-911" data-mp="1e6py81lboogk" />
                    Opretter …
                  </> : 'Opret tilbud'}
              </Button>

              {enquiry.caseId ? <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/sager/${enquiry.caseId}`)} data-id="element-912" data-mp="14f3efgux5h27">
                  Åbn sagen
                </Button> : <Button variant={enquiry.status === 'Ny henvendelse' ? 'secondary' : 'primary'} size="sm" disabled={working === enquiry.id} onClick={async () => {
            setWorking(enquiry.id);
            setActionError('');
            const created = await createCaseFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Sagen kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/sager/${created.id}`);
          }} data-id="element-913" data-mp="1wz21cqjsvtrr">
                  {working === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-914" data-mp="1a9sc7ijbcvf6" />
                      Opretter …
                    </> : 'Opret sag'}
                </Button>}
              {enquiry.phone && <LinkButton href={telHref(enquiry.phone)} variant="secondary" size="sm" data-id="element-915" data-mp="3tgd9f8o39ij">
                  Ring
                </LinkButton>}
              {enquiry.email && <LinkButton href={`mailto:${enquiry.email}`} variant="secondary" size="sm" data-id="element-916" data-mp="3ng8g3xvv4agp">
                  Send e-mail
                </LinkButton>}
              <button type="button" onClick={() => {
            setConfirming(enquiry);
            setDeleteError('');
            setDeleted(false);
            setAccepted(false);
            setActionError('');
          }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-917" data-mp="57zq7a4aviqd">
                Slet henvendelse
              </button>
            </div>
          </li>)}
      </ul>

      {confirming && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-titel" data-id="element-918" data-mp="2l204phl5tjlp">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-919" data-mp="j1mwlp2quqg4">
            <h2 id="slet-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-920" data-mp="a3avgt494a9f">
              Er du sikker på, at du vil slette denne henvendelse?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-921" data-mp="a3udkgjngf5">
              Henvendelsen fra {confirming.name} og dens billeder slettes permanent.
            </p>

            {deleteError && <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-922" data-mp="3mk7uk280lvve">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-923" data-mp="3dhoutqzizzdd" />
                {deleteError}
              </p>}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-924" data-mp="1t28trj9kni1x">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setDeleteError('');
            const result = await deleteEnquiry(confirming);
            setDeleting(false);
            if (!result.ok) {
              setDeleteError(result.error ?? 'Henvendelsen kunne ikke slettes. Prøv igen.');
              return;
            }
            setConfirming(null);
            setDeleted(true);
          }} data-id="element-925" data-mp="1xxfs0bk2nujg">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-926" data-mp="qknthrj1hlui" />
                    Sletter …
                  </> : 'Slet henvendelse'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirming(null)} data-id="element-927" data-mp="1yt34cqgzcqll">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}

      {!dataLoading && enquiries.length === 0 && <p className="mt-8 rounded-card border border-dashed border-line bg-white p-10 text-center text-sm text-ink-muted" data-id="element-928" data-mp="23nfqj89ucg11">
          Der er ingen henvendelser endnu.
        </p>}
    </div>;
}